import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StanInput, StanModal, StanPhoneInput } from '@/components';
import type { PhoneChange } from '@/components/core-components/StanPhoneInput/StanPhoneInput.types';
import type { CustomerProfile, UpdateCustomerPayload } from '../types/customer';
import { type EditCustomerFormData, editCustomerSchema } from '../validations/customerForm';
import { CustomerTagsField } from './CustomerTagsField';
import IcUser from '@/assets/icons/ic-user-01.svg';
import IcMail from '@/assets/icons/ic-mail.svg';

interface EditCustomerModalProps {
    isOpen: boolean;
    customer: CustomerProfile;
    onClose: () => void;
    onSubmit: (payload: UpdateCustomerPayload) => void;
    isLoading?: boolean;
}

export function EditCustomerModal({ isOpen, customer, onClose, onSubmit, isLoading = false }: EditCustomerModalProps) {
    const form = useForm<EditCustomerFormData>({
        resolver: zodResolver(editCustomerSchema),
        defaultValues: {
            fullName: '',
            email: '',
            phoneNumber: '',
        },
    });

    useEffect(() => {
        if (!isOpen) {
            form.reset();
            return;
        }
        form.reset({
            fullName: customer.fullName ?? '',
            email: customer.email ?? '',
            phoneNumber: customer.phoneNumber ?? '',
        });
    }, [customer, isOpen, form]);

    const handleSubmit = (data: EditCustomerFormData) => {
        onSubmit({
            fullName: data.fullName,
            email: data.email,
            phoneNumber: data.phoneNumber || undefined,
        });
    };

    return (
        <StanModal
            id='edit-customer-modal'
            isOpen={isOpen}
            title='Edit Customer'
            primaryActionLabel='Save'
            primaryActionLoading={isLoading}
            onPrimaryAction={form.handleSubmit(handleSubmit)}
            onClose={onClose}
            secondaryActionLabel='Cancel'
            onSecondaryAction={onClose}
            aria-label='Edit customer information'
        >
            <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
                <Controller
                    name='fullName'
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <StanInput
                            id='edit-customer-full-name'
                            name='edit-customer-full-name'
                            startIcon={IcUser}
                            placeholder='Name'
                            value={field.value}
                            onChange={field.onChange}
                            error={Boolean(fieldState.error)}
                            hintText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    name='email'
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <StanInput
                            id='edit-customer-email'
                            name='edit-customer-email'
                            type='email'
                            startIcon={IcMail}
                            placeholder='name@email.com'
                            value={field.value}
                            onChange={field.onChange}
                            error={Boolean(fieldState.error)}
                            hintText={fieldState.error?.message}
                        />
                    )}
                />
                <Controller
                    name='phoneNumber'
                    control={form.control}
                    render={({ field, fieldState }) => (
                        <StanPhoneInput
                            id='edit-customer-phone'
                            name='edit-customer-phone'
                            value={field.value || ''}
                            onChange={(change: PhoneChange) => {
                                const value = change.valid ? change.e164 : change.national;
                                field.onChange(value);
                            }}
                            error={Boolean(fieldState.error)}
                            hintText={fieldState.error?.message}
                        />
                    )}
                />
                <CustomerTagsField userFanId={customer.id} />
            </form>
        </StanModal>
    );
}
